import * as React from "react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BAVButton from "./BAVButton";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import BAVSlider from "./BAVSlider";
import {
  Typography,
  FormControl,
  Container,
  Paper,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { msalInstance } from "../index";
import { getUserToken } from "../utils/AuthTokenReader.jsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const queryClient = new QueryClient();

export default function BAVStandingOrderForm() {
  return (
    <QueryClientProvider client={queryClient}>
      <CreateStandingOrder />
    </QueryClientProvider>
  );
}

function CreateStandingOrder() {
  var readOnly;

  const [bankAccount, setBankAccount] = useState("");
  const [customAccount, setCustomAccount] = useState("");
  const isCustomAccount = bankAccount === "99";
  const [soExcutionDate, setSoExcutionDate] = useState("28");
  const [periodicityValue, setPeriodicityValue] = useState("1");
  const [missingMandatoryValue, setMissingMandatoryValue] = useState({
    state: false,
    text: "",
  });
  const [startDateValue, setStartDateValue] = useState(null);
  const [endDateValue, setEndDateValue] = useState(null);
  //const [responses, setResponses] = useState();
  const navigate = useNavigate();
  const location = useLocation();
  const account = msalInstance.getActiveAccount();

  if (!location.state) {
    // Testmodus ohne Übergabeparameter
    var entrytype = 2;
    var entryid = "9";
    var readOnly = false;
  } else {
    var kd = location.state.kunde;
    var entrytype = kd.entryType;
    var entryid = kd.entryId;
    var readOnly = location.state.readOnly;
  }

  useEffect(() => {
    initToken();
  }, []);

  useEffect(() => {
    //console.log(startDateValue);
  }, [startDateValue]);

  // Auth Token
  const [options, setOptions] = useState();
  const [token, setToken] = useState();
  const initToken = async () => {
    const ltoken = await getUserToken();
    setToken(ltoken);
    const headers = new Headers();
    const bearer = `Bearer ${ltoken.accessToken}`;
    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    setOptions({ headers: headers });
  };
  const reInitToken = new Promise((resolve, reject) => {
    if (token && token.expiresOn < Date.now()) {
      initToken().then((res) => {
        resolve("initToken");
      });
    } else {
      resolve("tokenValid");
    }
  });
  const {
    isLoading,
    data: responses,
    refetch,
  } = useQuery("StandingOrderData", fetchStandingOrderData, {
    enabled: !!options,
    cacheTime: 0,
  });

  const { isLoading2, data: accounts } = useQuery(
    "CustomerAccountData",
    fetchCustomerAccounts,
    { enabled: !!options, cacheTime: 0 },
  );

  useEffect(() => {
    if (!responses || !accounts || isLoading2) {
      return;
    }
    const v3 = responses?.[0]?.value_3;
    if (typeof v3 === "string" && v3.includes(";")) {
      const raw = String(v3 ?? "").split(";");
      while (raw.length && raw[raw.length - 1] === "") raw.pop();

      const [pRaw = "", aRaw = "", dRaw = "28", dtsRaw = null, dteRaw = null] =
        raw;
      const toNull = (v) => (v === "" ? null : v);

      const p = pRaw;
      const a = aRaw;
      const d = dRaw;
      const dts = toNull(dtsRaw);
      const dte = toNull(dteRaw);

      //robustes Parsen der Datumsstrings zu dayjs (unterstützt ISO + DD/MM/YYYY)
      const parseDate = (val) =>
        val
          ? dayjs(val, ["YYYY-MM-DD", "DD/MM/YYYY"], true) // strict parsing
          : null;

      const startDt = parseDate(dts);
      const endDt = parseDate(dte);

      setPeriodicityValue(p || "1");
      setSoExcutionDate(d || "28");
      setStartDateValue(startDt && startDt.isValid() ? startDt : null);
      setEndDateValue(endDt && endDt.isValid() ? endDt : null);

      // Ist der gespeicherte Account keiner der festen Optionen? -> Custom
      const fixedOptions = new Set(accounts?.map((acc) => String(acc.ktLnr)));

      if (!Array.isArray(accounts) && isLoading2) {
        setBankAccount("");
        setCustomAccount("");
        return;
      }

      if (a && !fixedOptions.has(String(a)) && a !== "98") {
        setBankAccount("99");
        setCustomAccount(a);
        setSoExcutionDate(d);
        setStartDateValue(startDt && startDt.isValid() ? startDt : null);
        setEndDateValue(endDt && endDt.isValid() ? endDt : null);
      } else {
        setBankAccount(a || "");
        setCustomAccount("");
        setSoExcutionDate(d);
        setStartDateValue(startDt && startDt.isValid() ? startDt : null);
        setEndDateValue(endDt && endDt.isValid() ? endDt : null);
      }
    } else {
      setPeriodicityValue("1");
      setBankAccount("");
      setCustomAccount("");
      setSoExcutionDate("28");
      setStartDateValue(null);
      setEndDateValue(null);
    }
  }, [responses, accounts]);

  useEffect(() => {
    if (missingMandatoryValue.state && isStandingOrderSectionValid()) {
      setMissingMandatoryValue({ state: false, text: "" });
    }
  }, [responses, periodicityValue, bankAccount, customAccount, soExcutionDate]);

  function handlePeriodicityChange(e) {
    const p = String(e.target.value ?? "");
    setPeriodicityValue(p);
    saveStandingOrderSettings(p, bankAccount, soExcutionDate, customAccount);
  }
  function handleAccountChange(e) {
    const a = String(e.target.value ?? "");
    setBankAccount(a);

    // Wenn ein fixes Konto gewählt wird, sofort speichern
    // (kein Abhängen von isCustomAccount/bankAccount-States beim Speichern)
    if (a !== "99") {
      setCustomAccount(""); // Freitext leeren
      saveStandingOrderSettings(
        periodicityValue,
        a,
        soExcutionDate,
        startDateValue,
        endDateValue,
        customAccount,
      );
    }
    // Wenn "Anderes" gewählt wird, noch NICHT automatisch speichern.
    // Speichern erfolgt über Button "Übernehmen" (oder optional onBlur im Textfeld).
  }

  function handleSODateChange(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setSoExcutionDate(digitsOnly);
  }

  function handleStartDateChange(newValue) {
    setStartDateValue(newValue);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      newValue,
      endDateValue,
      customAccount?.trim(),
    );
  }
  function handleEndDateChange(newValue) {
    setEndDateValue(newValue);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      startDateValue,
      newValue,
      customAccount?.trim(),
    );
  }

  function handleStartDateBlur(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setStartDateValue(digitsOnly);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      digitsOnly,
      endDateValue,
      customAccount.trim(),
    );
  }
  function handleEndDateBlur(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setEndDateValue(digitsOnly);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      soExcutionDate,
      startDateValue,
      digitsOnly,
      customAccount.trim(),
    );
  }

  function handleSOBlur(e) {
    let digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digitsOnly) {
      const n = Math.max(1, Math.min(31, Number(digitsOnly)));
      digitsOnly = String(n);
    }
    setSoExcutionDate(digitsOnly);
    saveStandingOrderSettings(
      periodicityValue,
      bankAccount,
      digitsOnly,
      startDateValue,
      endDateValue,
      customAccount.trim(),
    );
  }

  function handleChangeSparplan(props, newValue) {
    var valueField = props.valueField ? props.valueField : "value_1";
    var entry = responses[0];
    entry[valueField] = newValue + "";
    saveChange(entry);
  }
  function saveStandingOrderSettings(p, a, d, dts, dte, customOverride) {
    if (readOnly) return;

    // Entscheide ausschließlich anhand der übergebenen Parameter,
    // nicht anhand von isCustomAccount/bankAccount (verhindert Race-Conditions).
    const accountToSave = a === "99" ? (customOverride ?? "") : (a ?? "");

    //const combined = `${p ?? ""};${accountToSave};${d ?? ""};${dts};${dte};`; // value_3 immer als String "<p>;<konto>"
    const normalize = (v) => (v == null ? "" : String(v));
    const toStrDate = (v) =>
      v && dayjs.isDayjs(v) ? v.format("YYYY-MM-DD") : normalize(v);

    const parts = [
      normalize(p ?? ""),
      normalize(accountToSave ?? ""),
      normalize(d ?? "28"),
      toStrDate(dts),
      toStrDate(dte),
    ];

    while (parts.length && parts[parts.length - 1] === "") {
      parts.pop();
    }
    const combined = parts.join(";");

    let entry = responses[0];

    // Vorhandenen Datensatz aktualisieren
    entry.value_3 = combined;
    saveChange(entry); // persistiert in der DB und triggert refetch()
  }

  function saveChange(postData) {
    if (readOnly) {
      return;
      }
      // Für ältere Profilierungen, die noch keine Daten zum Dauerauftrag haben, 250 initialisieren 
      if (postData.value_2 == null) {
          postData.value_2 = "250";
      }


    if (postData.entryDetailId > 0) {
      let requestOptions = {
        method: "PUT",
        headers: options.headers,
        body: JSON.stringify(postData),
      };
      reInitToken.then((res) => {
        fetch("/entrydetails/" + postData.entryDetailId, requestOptions)
          .then(() => {
            refetch();
          })
          .catch((error) => {
            refetch();
          });
      });
    } else {
        var entry = {
            entryDetailId: "0",
            entryId: postData.entryId,
            entryDetailType: postData.entrytype,
            questionId: postData.questionId,
            responseId: postData.responseId,
            value_1: postData?.value_1 ?? "0",
            value_2: postData.value_2 ?? "250",
            value_3: postData.value_3
        };
        let requestOptions = {
            method: "POST",
            headers: options.headers,
            body: JSON.stringify(entry),
        };
        reInitToken.then((res) => {
            fetch("/entrydetails", requestOptions)
                .then(() => {
                    refetch();
                })
                .catch((error) => {
                    refetch();
                });
        });
    }
  }

  function handleSaveCustomAccount() {
    if (readOnly) return;

    // Nur speichern, wenn "Anderes Konto" aktiv ist und ein Freitext vorliegt
    if (bankAccount === "99" && customAccount.trim() !== "") {
      // Wir übergeben den Freitext explizit (customOverride), um stale State zu vermeiden.
      saveStandingOrderSettings(
        periodicityValue,
        "99",
        soExcutionDate,
        startDateValue,
        endDateValue,
        customAccount.trim(),
      );

      // Optional: Fehlermeldung löschen, wenn vorher blockiert wurde
      if (missingMandatoryValue.state) {
        setMissingMandatoryValue({ state: false, text: "" });
      }
    } else {
      // Falls leer: Fehlermeldung setzen
      setMissingMandatoryValue({
        state: true,
        text: "Bitte geben Sie ein Konto ein und speichern Sie es über «Übernehmen».",
      });
    }
  }
  function isCustomAccountIncomplete() {
    // Ist "Anderes Konto" gewählt?
    const isCustomAccountlocal = bankAccount === "99";
    if (!isCustomAccountlocal) return false;

    // Freitext muss befüllt sein
    if (!customAccount || customAccount.trim() === "") {
      return true;
    }
    const v3 = responses?.[0]?.value_3; // aktuelle Antwort
    if (typeof v3 !== "string" || !v3.includes(";")) {
      return true; //
    }
    const [pSaved, aSaved] = v3.split(";");
    if ((aSaved ?? "").trim() !== customAccount.trim()) {
      return true;
    }
    return false;
  }

  function handleBackToCustomer() {
    navigate(-1);
  }
  function handleAuswertung() {
    window.open("/BAVReport/?entryId=" + kd.entryId + "&standingOrderOnly=1");
  }

  function fetchStandingOrderData() {
    try {
      return reInitToken.then((res) => {
        return fetch(
          "/profileform/standingOrderResponse/" + entryid,
          options,
        ).then((res) => {
          return res.json();
        });
      });
    } catch (e) {
      initToken();
    }
  }

  function fetchCustomerAccounts() {
    try {
      return reInitToken.then((res) => {
        return fetch("/account?pEntryId=" + entryid + "&pMode=2", options).then(
          (res) => {
            return res.json();
          },
        );
      });
    } catch (e) {
      initToken();
    }
  }
  function getCurrentStandingOrderAmount() {
    const base = responses?.[0];
    if (!base) return 0;

    const v2 = Number(base?.value_2 ?? 0);
    return Number.isNaN(v2) ? 0 : v2;
  }
  function isStandingOrderSectionValid() {
    const amount = getCurrentStandingOrderAmount();
    const hasAmount = amount > 0;

    // Periodizität gewählt?
    const hasPeriodicity = (periodicityValue ?? "") !== "";

    // Konto gewählt & (falls "Anderes") korrekt gespeichert?
    const hasAccount =
      (bankAccount ?? "") !== "" && !isCustomAccountIncomplete();

    return hasAmount && hasPeriodicity && hasAccount;
  }

  if (!responses || isLoading || !accounts || isLoading2) {
    return <div>Loading...</div>;
  }
  if (responses && responses.length == 0) {
    return <div>Keine Profilierung für diesen Kunden vorhanden</div>;
  }
  const disableCreate = !isStandingOrderSectionValid();
  return (
    <>
      <main className="main">
        <Container className="mainContainer">
          <Paper>
            <div className="pageContainer">
              <Box
                sx={{
                  width: "93%",
                  borderRadius: "20px",
                  border: "1px solid #cbd1d663",
                  marginLeft: "20px",
                  typography: "body2",
                  marginTop: "20px",
                }}
                className="bavSlider"
              >
                <BAVSlider
                  min={50}
                  max={10000}
                  step={50}
                  response={responses[0]}
                  key={responses[0].responseId + "1"}
                  valueField="value_2"
                  title={"Betrag"}
                  callback={handleChangeSparplan}
                  readOnly={readOnly}
                  sliderWidth={300}
                  defaultValue={250}
                />
                <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                  <InputLabel id="lblPeriodicity">
                    <Typography variant="body2">Periodizität</Typography>
                  </InputLabel>
                  <Select
                    labelId="lblPeriodicity"
                    id="cmbPeriodicity"
                    value={periodicityValue}
                    label="Periodizität"
                    onChange={(e) => handlePeriodicityChange(e)}
                    name="cmbPeriodicity"
                  >
                    <MenuItem value={"1"}>
                      <Typography variant="body2">monatlich</Typography>
                    </MenuItem>
                    <MenuItem value={"2"}>
                      <Typography variant="body2">quartalsweise</Typography>
                    </MenuItem>
                    <MenuItem value={"3"}>
                      <Typography variant="body2">halbjährlich</Typography>
                    </MenuItem>
                    <MenuItem value={"4"}>
                      <Typography variant="body2">jährlich</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl sx={{ m: 1, minWidth: 700 }} size="small">
                  <InputLabel id="lblAccount">
                    <Typography variant="body2">
                      Belastungskonto Dauerauftrag
                    </Typography>
                  </InputLabel>
                  <Select
                    labelId="lblAccount"
                    id="cmbAccount"
                    value={bankAccount}
                    onChange={handleAccountChange}
                    label="Belastungskonto Dauerauftrag"
                    name="cmbAccount"
                  >
                    <MenuItem disabled disableGutters>
                      <Box
                        sx={{
                          px: 2, // horizontaler Innenabstand
                          display: "grid",
                          gridTemplateColumns: "150px 150px 150px 100px 100px",
                          columnGap: 2,
                          width: 1,
                          color: "text.secondary",
                          fontWeight: 600,
                          typography: "caption",
                        }}
                      >
                        <span>Konto</span>
                        <span>Rubrik</span>
                        <span>Kontoart</span>
                        <span>Währung</span>
                        <Box component="span" sx={{ textAlign: "right" }}>
                          Saldo
                        </Box>
                      </Box>
                    </MenuItem>

                    {accounts?.map((acc) => (
                      <MenuItem
                        value={acc.ktLnr}
                        key={acc.ktLnr}
                        disableGutters
                      >
                        <Box
                          sx={{
                            px: 2,
                            display: "grid",
                            gridTemplateColumns:
                              "150px 150px 150px 100px 100px",
                            columnGap: 2,
                            alignItems: "center",
                            width: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {acc.editKtNr}
                          </Typography>
                          <Typography variant="body2">{acc.rubrik}</Typography>
                          <Typography variant="body2">
                            {acc.ktArtBez}
                          </Typography>
                          <Typography variant="body2" noWrap>
                            {acc.wrgBez}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {acc.saldoKWrg}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                    <MenuItem value="99" key="99">
                      <Typography variant="body2">
                        Anderes bestehendes BAV-Konto
                      </Typography>
                    </MenuItem>
                    <MenuItem value="98" key="98">
                      <Typography variant="body2">
                        Neu zu eröffnendes Konto gemäss Orion-Auftrag
                      </Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
                {isCustomAccount && (
                  <>
                    <Box sx={{ display: "flex", alignItems: "left", m: 1 }}>
                      <TextField
                        id="txtCustomAccount"
                        label="Anderes Konto"
                        variant="outlined"
                        size="small"
                        value={customAccount}
                        onChange={(e) => {
                          const val = e.target.value;
                          // nur Zahlen und '.' erlauben
                          const filtered = val
                            .replace(/[^0-9.]/g, "")
                            .slice(0, 16);
                          setCustomAccount(filtered);
                        }}
                        disabled={readOnly}
                        inputProps={{
                          // ← direkt am <input>
                          maxLength: 16,
                          inputMode: "decimal",
                          pattern: "[0-9.]*",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{ minWidth: 240 }}
                        InputProps={{
                          sx: {
                            typography: "body2",
                            "& .MuiOutlinedInput-notchedOutline legend": {
                              width: "90px",
                              maxWidth: "100%",
                            },
                          },
                        }}
                      />

                      <BAVButton
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveCustomAccount()}
                        disabled={
                          readOnly ||
                          customAccount.trim() === "" ||
                          !isCustomAccountIncomplete()
                        }
                        sx={{ ml: 1 }}
                      >
                        Übernehmen
                      </BAVButton>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "left", m: 1 }}>
                      <div style={{ fontSize: 10, marginBottom: 10 }}>
                        <i>
                          Handelt es sich beim Belastungskonto um ein Konto
                          eines anderen Partners ist eine vorhandene Vollmacht
                          zwingend
                        </i>
                      </div>
                    </Box>
                  </>
                )}

                <Box
                  sx={{ display: "flex", alignItems: "start", gap: 1, m: 1 }}
                >
                  <Box component="div" sx={{ fontWeight: 700, minWidth: 180 }}>
                    Ausführungstag
                  </Box>

                  <Box component="div" sx={{ minWidth: 70 }}>
                    jeweils am
                  </Box>
                  <TextField
                    id="txtExcutionDate"
                    variant="standard"
                    size="small"
                    value={soExcutionDate}
                    onChange={(e) => handleSODateChange(e)}
                    onBlur={handleSOBlur}
                    inputProps={{
                      // ← direkt am <input>
                      maxLength: 2,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    disabled={readOnly}
                    sx={{
                      maxWidth: 20,
                      alignSelf: "flex-start",
                      "& .MuiInputBase-input": {
                        fontSize: 12,
                        paddingY: 0, // extra kompakt
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          disablePointerEvents
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            ml: 0,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "inherit", lineHeight: "inherit" }}
                          >
                            .
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    component="div"
                    sx={{
                      display: "inline-block", // macht den Block als Einheit
                      whiteSpace: "normal", // Block selbst darf intern umbrechen (bei schmalen Layouts)
                      // optional: für sehr lange Texte Worttrennung erlauben
                      overflowWrap: "anywhere",
                    }}
                  >
                    eines Monats (in Abhängigkeit der oben angegebenen
                    Ausführungsperiodizität)
                  </Box>
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}
                >
                  <Box component="div" sx={{ fontWeight: 400, minWidth: 180 }}>
                    Startdatum
                  </Box>
                  <Box sx={{ minWidth: 300 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={startDateValue}
                        onChange={(newValue) => handleStartDateChange(newValue)}
                        //onBlur={handleStartDateBlur}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            placeholder: "nächstmögliche Ausführung",
                            fullWidth: true,
                            size: "small",
                            label: undefined, // explizit KEIN Label

                            InputLabelProps: { shrink: false },

                            sx: {
                              "& .MuiInputBase-input::placeholder": {
                                color: "black",
                                opacity: 1,
                                fontSize: "0.875rem",
                              },
                              "& .MuiInputBase-input": {
                                backgroundColor: "transparent", // falls bei dir grauer BG sichtbar war
                                fontSize: "0.875rem",
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <BAVButton
                    size="small"
                    variant="text"
                    onClick={() => {
                      setStartDateValue(null);
                      saveStandingOrderSettings(
                        periodicityValue,
                        bankAccount,
                        soExcutionDate,
                        null,
                        endDateValue,
                        customAccount?.trim(),
                      );
                    }}
                    disabled={!startDateValue}
                  >
                    löschen
                  </BAVButton>
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, m: 1 }}
                >
                  <Box component="div" sx={{ fontWeight: 400, minWidth: 180 }}>
                    Enddatum
                  </Box>
                  <Box sx={{ minWidth: 300 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={endDateValue}
                        onChange={(newValue) => handleEndDateChange(newValue)}
                        //onBlur={handleEndDateBlur}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            placeholder: "bis auf Widerruf",
                            fullWidth: true,
                            size: "small",
                            label: undefined, // explizit KEIN Label
                            InputLabelProps: { shrink: false },
                            sx: {
                              "& .MuiInputBase-input::placeholder": {
                                color: "black",
                                opacity: 1,
                                fontSize: "0.875rem",
                              },
                              "& .MuiInputBase-input": {
                                backgroundColor: "transparent",
                                fontSize: "0.875rem",
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <BAVButton
                    size="small"
                    variant="text"
                    onClick={() => {
                      setEndDateValue(null);
                      saveStandingOrderSettings(
                        periodicityValue,
                        bankAccount,
                        soExcutionDate,
                        startDateValue,
                        null,
                        customAccount?.trim(),
                      );
                    }}
                    disabled={!endDateValue}
                  >
                    löschen
                  </BAVButton>
                </Box>
                <Box sx={{ flex: "1 1 auto", minHeight: "20vh" }} />
                <footer className="footer">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end", // <-- schiebt den gesamten Inhalt nach rechts
                      gap: 1,
                      p: 2,
                    }}
                  >
                    <BAVButton
                      variant="outlined"
                      onClick={handleBackToCustomer}
                    >
                      Zurück zum Kunden
                    </BAVButton>
                    <BAVButton
                      variant="outlined"
                      onClick={handleAuswertung}
                      disabled={disableCreate}
                    >
                      {" "}
                      Dauerauftrag erstellen
                    </BAVButton>
                  </Box>
                </footer>
              </Box>
            </div>
          </Paper>
        </Container>
        {missingMandatoryValue.state && (
          <>
            <p />
            <Alert severity="error">{missingMandatoryValue.text}</Alert>
          </>
        )}
      </main>
    </>
  );
}
